import Vue from 'vue';
import { runtime } from './bridge';

export type SiteUser = { id: string; username: string; nickname: string; avatarUrl?: string };
export type CloudPlatformSession = { id: string; platform: 'netease' | 'qq'; profile: { id: string; nickname: string; avatarUrl: string }; expiresAt: number; label?: string; createdAt: number; updatedAt: number };
export const siteAccount = Vue.observable({ ready: false, busy: false, user: null as SiteUser | null, csrfToken: '', expiresAt: 0, error: '', sessions: [] as CloudPlatformSession[], revision: 0 });
let bootstrap: Promise<void> | null = null;
let operationGeneration = 0;
let listGeneration = 0;
let refreshGeneration = 0;
let pendingRefresh = false;
const revisionKey = 'simple-music.site-account-revision.v1';
const channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel(revisionKey) : null;
function notifyAccountChange() {
  const revision = crypto.randomUUID();
  if (channel) channel.postMessage({ revision });
  else try { localStorage.setItem(revisionKey, revision); } catch { /* Same-tab updates still apply. */ }
}
function receiveAccountChange() {
  if (siteAccount.busy) { pendingRefresh = true; return; }
  void refreshSiteAccount().catch(() => {});
}
if (channel) channel.onmessage = event => { if (typeof event.data?.revision === 'string') receiveAccountChange(); };
else window.addEventListener('storage', event => { if (event.key === revisionKey && event.newValue) receiveAccountChange(); });
function releaseBusy(generation: number) {
  if (generation !== operationGeneration) return;
  siteAccount.busy = false;
  if (pendingRefresh) { pendingRefresh = false; receiveAccountChange(); }
}
const changedSession = () => new DOMException('云端账号已切换，本次操作已取消。', 'AbortError');
const ownerSnapshot = () => ({ id: siteAccount.user?.id || '', revision: siteAccount.revision, generation: operationGeneration });
const sameOwner = (owner: ReturnType<typeof ownerSnapshot>) => owner.id === (siteAccount.user?.id || '') && owner.revision === siteAccount.revision && owner.generation === operationGeneration;
async function request(action: string, method = 'GET', body?: unknown, extraHeaders?: Record<string,string>) {
  const owner = ownerSnapshot();
  try { return await runtime.api(`/api/site-account/${action}`, {
    method, credentials: 'same-origin', headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(siteAccount.csrfToken ? { 'x-csrf-token': siteAccount.csrfToken } : {}),
      ...extraHeaders,
    }, ...(body === undefined ? {} : { body: JSON.stringify(body) }), signal: AbortSignal.timeout(20000),
  }); } catch (error) {
    if ((error as { code?: string }).code === 'SITE_ACCOUNT_AUTH_REQUIRED' && sameOwner(owner)) { operationGeneration++; assignSession({ authenticated: false }); siteAccount.sessions = []; siteAccount.busy = false; }
    throw error;
  }
}
function assignSession(data: any, preserveOwner = false) {
  const previousId = siteAccount.user?.id || '', previousCsrf = siteAccount.csrfToken;
  siteAccount.user = data.authenticated === false ? null : data.user || null;
  siteAccount.csrfToken = data.csrfToken || ''; siteAccount.expiresAt = data.expiresAt || 0;
  siteAccount.ready = true;
  // Editing a nickname/avatar must not invalidate unrelated platform requests.
  if (previousId !== (siteAccount.user?.id || '') || (!preserveOwner && previousCsrf !== siteAccount.csrfToken)) siteAccount.revision++;
}
export async function refreshSiteAccount() {
  const operation = operationGeneration, generation = ++refreshGeneration;
  const previousId = siteAccount.user?.id || '', previousCsrf = siteAccount.csrfToken;
  const data = await request('status');
  if (operation !== operationGeneration || generation !== refreshGeneration) return;
  assignSession(data, previousId === (data.user?.id || ''));
  if (previousId !== (siteAccount.user?.id || '') || previousCsrf !== siteAccount.csrfToken) {
    operationGeneration++;
    siteAccount.sessions = [];
    if (siteAccount.user) {
      await listCloudSessions();
      const { loadCloudAccounts } = await import('./accounts');
      await loadCloudAccounts();
    }
  }
}
export async function restoreSiteAccount() {
  if (bootstrap) return bootstrap;
  if (siteAccount.ready) return;
  const generation = operationGeneration;
  const restoring: Promise<void> = (async () => {
    try { const data = await request('status'); if (generation !== operationGeneration) return; assignSession(data); if (siteAccount.user) await listCloudSessions(); }
    catch (error) { if (generation === operationGeneration) { siteAccount.error = (error as Error).message; siteAccount.ready = true; } }
  })().finally(() => { if (bootstrap === restoring) bootstrap = null; });
  bootstrap = restoring;
  return bootstrap;
}
export async function authenticateSiteAccount(mode: 'login' | 'register', username: string, password: string, nickname: string) {
  if (siteAccount.busy) return;
  const generation = ++operationGeneration;
  siteAccount.busy = true; siteAccount.error = '';
  try { const data = await request(mode, 'POST', { username, password, nickname }); if (generation !== operationGeneration) throw changedSession(); assignSession(data); notifyAccountChange(); await listCloudSessions(); }
  catch (error) { if (generation === operationGeneration) siteAccount.error = (error as Error).message; throw error; }
  finally { releaseBusy(generation); }
}
export async function logoutSiteAccount() {
  const generation = ++operationGeneration;
  siteAccount.busy = true; siteAccount.error = '';
  try { await request('logout', 'POST', {}); if (generation !== operationGeneration) throw changedSession(); assignSession({ authenticated: false }); siteAccount.sessions = []; notifyAccountChange(); }
  catch (error) { if (generation === operationGeneration) siteAccount.error = (error as Error).message; throw error; }
  finally { releaseBusy(generation); }
}
export async function updateSiteProfile(profile: { nickname?: string; avatarUrl?: string | null }) {
  if (!siteAccount.user) throw changedSession();
  if (siteAccount.busy) throw new Error('请等待当前账号操作完成');
  const generation = operationGeneration, owner = ownerSnapshot();
  siteAccount.busy = true;
  try {
    const data = await request('profile', 'PATCH', profile);
    if (!sameOwner(owner)) throw changedSession();
    refreshGeneration++;
    assignSession(data); notifyAccountChange();
    return siteAccount.user;
  } finally { releaseBusy(generation); }
}
export async function changeSitePassword(oldPassword: string, newPassword: string) {
  if (!siteAccount.user) throw changedSession();
  if (siteAccount.busy) throw new Error('请等待当前账号操作完成');
  const generation = operationGeneration, owner = ownerSnapshot();
  siteAccount.busy = true;
  try {
    const data = await request('password', 'POST', { oldPassword, newPassword });
    if (!sameOwner(owner)) throw changedSession();
    refreshGeneration++;
    // This session rotates its HTTP cookie, but its platform-vault owner stays
    // the same. Do not strand active platform contexts on an old owner revision.
    assignSession(data, true); notifyAccountChange();
  } finally { releaseBusy(generation); }
}
export async function listCloudSessions() {
  const owner = ownerSnapshot(), generation = ++listGeneration;
  if (!owner.id) { siteAccount.sessions = []; return []; }
  const data = await request('platform-sessions');
  if (!sameOwner(owner)) throw changedSession();
  if (generation !== listGeneration) return siteAccount.sessions;
  siteAccount.sessions = data.entries || [];
  return siteAccount.sessions;
}
export async function saveCloudSession(entry: { platform: 'netease' | 'qq'; token: string; profile: object; expiresAt: number }) {
  const owner = ownerSnapshot();
  if (!owner.id) throw new Error('请先登录云端账号，再保存平台登录状态');
  const data = await request('platform-sessions', 'POST', entry);
  if (!sameOwner(owner)) throw changedSession();
  await listCloudSessions();
  if (!sameOwner(owner)) throw changedSession();
  return data.entry as CloudPlatformSession;
}
export async function openCloudSession(id: string) { const owner = ownerSnapshot(); if (!owner.id) throw new Error('请先登录云端账号'); const data = await request(`platform-sessions/${encodeURIComponent(id)}/open`, 'POST', {}); if (!sameOwner(owner)) throw changedSession(); return data; }
export async function deleteCloudSession(id: string) { const owner = ownerSnapshot(); if (!owner.id) throw new Error('请先登录云端账号'); await request(`platform-sessions/${encodeURIComponent(id)}`, 'DELETE'); if (!sameOwner(owner)) throw changedSession(); await listCloudSessions(); }
export async function readCloudLibraryRecord(id: string, key: string) { const owner = ownerSnapshot(); if (!owner.id) throw changedSession(); const data = await request(`platform-sessions/${encodeURIComponent(id)}/library/${encodeURIComponent(key)}`); if (!sameOwner(owner)) throw changedSession(); return data.record?.value || null; }
export async function writeCloudLibraryRecord(id: string, key: string, value: unknown) { const owner = ownerSnapshot(); if (!owner.id) throw changedSession(); await request(`platform-sessions/${encodeURIComponent(id)}/library/${encodeURIComponent(key)}`, 'PUT', { value }); if (!sameOwner(owner)) throw changedSession(); }
export async function writeCloudLibraryRecords(id: string, records: { key: string; value: unknown }[]) { const owner = ownerSnapshot(); if (!owner.id) throw changedSession(); await request(`platform-sessions/${encodeURIComponent(id)}/library`, 'PUT', { records }); if (!sameOwner(owner)) throw changedSession(); }
export async function readCloudLibraryIndex() { const owner=ownerSnapshot();if(!owner.id)throw changedSession();const data=await request('library-index');if(!sameOwner(owner))throw changedSession();return data; }
export async function writeCloudLibraryIndex(backup: unknown, revision: number) { const owner=ownerSnapshot();if(!owner.id)throw changedSession();const data=await request('library-index','PUT',{backup},{'if-match':`"${revision}"`});if(!sameOwner(owner))throw changedSession();return data; }
